import { rgba } from 'polished';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { CityIcon, FriendsIcon, GroupsIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Highlight, Paragraph, Popover, tokens } from '@treams/treams-ui';

import Confirm from './Confirm';
import Delete from './Delete';
import Outlet from './Outlet';

const Name = styled(Box)`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Footnote = styled(Paragraph)`
	font-size: 12px;
	line-height: 14px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const GroupContainer = styled(Flex)<{
	$enabled?: boolean;
	$selected?: boolean;
	$color: string;
	$disabled?: boolean;
}>`
	border-radius: 5px;
	background: ${(props) =>
		props.$disabled ? props.theme.colors.lightBlueLight : props.theme.colors.white};
	box-shadow:
		0px 0px 0px 1px ${rgba(tokens.text.secondary, 0.25)},
		0px 2px 4px ${rgba(tokens.text.secondary, 0.25)};
	width: 160px;
	height: 40px;
	padding: 0px 10px;
	font-size: 14px;
	line-height: 14px;
	align-items: center;
	border-left: 4px solid ${(props) => props.theme.colors[props.$color]};

	${(props) =>
		props.$enabled !== false &&
		`
	cursor: pointer;

	&:hover {
		box-shadow: 0px 0px 0px 1px ${rgba(tokens.text.secondary, 0.5)},
			0px 2px 4px ${rgba(tokens.text.secondary, 0.25)};
	}`}

	${(props) =>
		props.$selected &&
		`
		${Footnote} {
			color: ${rgba(props.theme.colors.white, 0.5)};
		}
		
		&, &:hover {
			background: ${props.theme.colors.primary};
			color: ${props.theme.colors.white};
			border-left-color: ${rgba(props.theme.colors.white, 0.5)};
			box-shadow: 0px 0px 0px 2px ${rgba(props.theme.colors.primary, 0.25)};
		}
	`}
`;

const Icon = styled(Box)<{ $selected?: boolean }>`
	svg {
		display: block;
		width: 20px;
		height: 20px;

		path {
			fill: ${(props) =>
				props.$selected ? rgba(props.theme.colors.white, 0.5) : tokens.text.secondary};
		}
	}
`;

type GroupProps = {
	group: graphql.Group;
	single: boolean;
	canSelect: boolean;
	query: string;
	parent?: boolean;
	hasChildren: boolean;
	recursive?: boolean;
	inlet?: boolean;
	outlet?: boolean;
	disabled?: boolean;
	onSelect?: (recursive: boolean) => void;
	onDeselect?: () => void;
};

export default function Group({
	group,
	single,
	canSelect,
	query,
	parent,
	hasChildren,
	recursive,
	inlet,
	outlet,
	disabled,
	onSelect,
	onDeselect,
}: GroupProps) {
	const { t } = useTranslation();
	const [selected, setSelected] = useState(false);

	return (
		<Popover.Target
			visible={selected}
			contents={
				<>
					{onSelect && !onDeselect && (
						<Confirm
							single={single}
							canSelect={canSelect}
							group={group}
							hasChildren={hasChildren}
							onSelect={(recursive) => {
								onSelect!(recursive);
								setSelected(false);
							}}
						/>
					)}
					{onDeselect && (
						<Delete
							single={single}
							group={group}
							recursive={recursive}
							onSelect={(recursive) => {
								onSelect!(recursive);
								setSelected(false);
							}}
							onDeselect={() => {
								onDeselect!();
								setSelected(false);
							}}
						/>
					)}
				</>
			}
			onDismiss={() => setSelected(false)}
		>
			{inlet && <Outlet top={0} selected={selected} />}
			<GroupContainer
				onClick={() => setSelected(true)}
				$selected={selected}
				$disabled={disabled}
				$color={
					parent
						? group.type === graphql.GroupType.Department
							? 'employee'
							: 'manager'
						: 'primary'
				}
			>
				<Box flexGrow={1} flexShrink={1} minWidth={0}>
					<Name>
						<Highlight query={query}>{group.name}</Highlight>
					</Name>
					{recursive && <Footnote>+ {t('groups.selector.group.underlying')}</Footnote>}
				</Box>
				<Icon $selected={selected} flexGrow={0} flexShrink={0} ml={2}>
					{!parent && <CityIcon />}
					{parent && group.type === graphql.GroupType.Department && <GroupsIcon />}
					{parent && group.type === graphql.GroupType.Team && <FriendsIcon />}
				</Icon>
			</GroupContainer>
			{outlet && <Outlet bottom={0} selected={selected} />}
		</Popover.Target>
	);
}

Group.Skeleton = function GroupSkeleton() {
	return (
		<GroupContainer $color="slateBlue" $enabled={false}>
			<Box flexGrow={1} flexShrink={1}>
				<Paragraph.Skeleton lines={1} width={136} />
			</Box>
		</GroupContainer>
	);
};
